import React, { FC, useEffect, useState } from 'react'
import t from 'src/translations/main'
import utils, { checkMode } from 'src/components/Organisms/Form/utils'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import storage from 'src/components/Core/Utils/storage'
import updateModal from 'src/components/Sections/Modal/updateModal'
import {
  useDirectorsDetails,
  useDirectorsDetailsUpdate,
  useDropdownOptions,
} from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Table from 'src/components/Organisms/Table/Table'
import Font from 'src/components/Core/Typography/Font/Font'
import ModalDirectorDetailsForm from './ModalDirectorDetails/ModalDirectorDetailsForm'

import {
  FormStyle,
  FormHeader,
  FormTitle,
  FormDescription,
  FormFooter,
  FormFooterActionsLeft,
  FormFooterActionsRight,
} from 'src/components/Organisms/Form/Form.styles'

import {
  ApiData,
  FormProps,
  TableConfigParam,
  TableObj,
} from '../../../../Pages/Onboarding/types'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import { getEndpointUrl } from 'src/components/Core/Utils/url_constants'
import Popup from 'src/components/Atoms/Popup/Popup'

const tableConfig: TableConfigParam[] = [
  { key: 'nameOfDirector' },
  { key: 'residentialAddress' },
  { key: 'dateOfBirth' },
  { key: 'directorCity' },
  { key: 'directorCountry' },
  { key: 'directorPostCode' },
  {
    name: 'pencil',
    color: 'ultraViolet',
  },
  {
    name: 'close',
    color: 'error',
  },
]

const tableColumns = [
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyDirectorsForm.table.col1'
    ),
    accessor: 'col1',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyDirectorsForm.table.col2'
    ),
    accessor: 'col2',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyDirectorsForm.table.col3'
    ),
    accessor: 'col3',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyDirectorsForm.table.col4'
    ),
    accessor: 'col4',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyDirectorsForm.table.col5'
    ),
    accessor: 'col5',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyDirectorsForm.table.col6'
    ),
    accessor: 'col6',
  },
  {
    Header: '',
    accessor: 'col7',
  },
  {
    Header: '',
    accessor: 'col8',
  },
]

let initialRenderFlag = true

const CompanyDirectorsForm: FC<FormProps> = props => {
  const [genericError, setGenericError] = useState({error: false, message: ''})
  const tableData = useDirectorsDetails()
  const updateTableData = useDirectorsDetailsUpdate()
  const { dropdownOptions } = useDropdownOptions()

  const applicationId = storage.get(storage?.keys?.onboarding?.applicationId)

  const submitForm = () => {
    const { submit } = props
    if (submit) submit({data: {}})
  }

  const { sendRequest, isLoading } = useFetch()

  const editDirectorDetails = (id: number) => {
    updateModal({
      title: t('onboarding.modals.DirectorsDetails.title'),
      modalState: 'open',
      content: (
        <ModalDirectorDetailsForm
          actionType='edit'
          action={action}
          tableConfig={tableConfig}
          postUrl={getEndpointUrl('DeliverableAOF/updatecompanydirectors04')}
          id={id}
          applicationId={applicationId}
        />
      ),
      removeFooter: true,
    })
  }

  const deleteDirector = (id: number) => {
    // @ts-ignore:next-line
    const modifyResponse = (data: ApiData, response) => {
      if (data && response.ok) {
        updateTableData((prevState: TableObj[]) => {
          return utils.deleteTableRow([...prevState], id)
        })
      }
      setGenericError({error: true, message: t('global.errorInOperation')})
      updateModal({ modalState: 'close' })
    }
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl('DeliverableAOF/deletecompanydirector04', applicationId, 'deleteShareholderDetails.json', `directorID=${id}`),
        method: 'DELETE',
      },
      modifyResponse,
      () => {
        setGenericError({error: true, message: t('global.errorInOperation')})
        updateModal({ modalState: 'close' })
      }
    )
  }

  const openConfirmDeleteModal = (id: number) => {
    updateModal({
      title: t('onboarding.modals.DirectorsDetails.delete.title'),
      modalState: 'open',
      content: (
        <Font>
          {t('onboarding.modals.DirectorsDetails.delete.content')}
        </Font>
      ),
      footerActions: [
        {
          label: t('components.forms.labels.yes'),
          version: 2,
          onClick: () => {
            deleteDirector(id)
          },
        },
        {
          label: t('components.forms.labels.no'),
          onClick: () => {
            updateModal({ modalState: 'close' })
          },
        },
      ],
    })
  }

  const action = (id: number, action: string) => {
    if (action === 'edit') editDirectorDetails(id)
    if (action === 'delete') openConfirmDeleteModal(id)
  }

  const createTableData = (
    apiData: ApiData,
    tableConfig: TableConfigParam[]
  ) => {
    const newTableData: TableObj[] = []
    apiData.forEach((rowDataObj: ApiData) => {
      // if (rowDataObj?.id) rowDataObj.id = rowDataObj?.id
      if (rowDataObj?.dateOfBirth) {
        rowDataObj.dateOfBirth = utils.convertDate(rowDataObj.dateOfBirth.replaceAll('/', '-'), true)
      }
      // @ts-ignore
      const rowDataObjMapped = {...rowDataObj, directorCountry: dropdownOptions.countries?.find(country => country?.index.toString() === rowDataObj?.directorCountry)?.label}
      const tableObj = utils.createTableObj(
        rowDataObjMapped,
        tableConfig,
        action
      )
      newTableData.push(tableObj)
    })

    return newTableData
  }

  useEffect(() => {
    const setFetchedDataHandler = (data: ApiData) => {
      if (data?.directors?.length !== 0) {
        updateTableData(
          createTableData(data.directors, tableConfig)
        )
      }
      initialRenderFlag = false
    }

    if (initialRenderFlag !== true) return

    sendRequest({
      scope: 'onboarding',
      url: getEndpointUrl('DeliverableAOF/getcompanydirectors04', applicationId, 'allDirectorsDetails.json')
    }, setFetchedDataHandler)
  }, [])

  useEffect(() => {
    if(tableData?.length === 0 && !initialRenderFlag){
      if(props.setOptionsList){
        props.setOptionsList(prevList => (
          prevList?.map((obj, index) => {
            if (index === 3) {
              obj.state = 'in_progress'
            }
            return obj
          })
        ))
      }

      if(props.setCompletedStepList){
        props.setCompletedStepList(prevList => (
          prevList?.map((value, index) => {
            return index === 3 ? false : value
          })
        ))
      }

    }

  },[tableData])

  if(initialRenderFlag && isLoading) return <Spinner removeFullscreen />

  return (
    <FormStyle>
      <FormHeader>
        <FormTitle>
          {t('onboarding.forms.CorporateAccount.CompanyDirectorsForm.title')}
        </FormTitle>
        <FormDescription>
          {t(
            'onboarding.forms.CorporateAccount.CompanyDirectorsForm.description'
          )}
        </FormDescription>
      </FormHeader>
      <Button
        version={2}
        label={t('onboarding.modals.DirectorsDetails.openButton')}
        onClick={() =>
          updateModal({
            title: t('onboarding.modals.DirectorsDetails.title'),
            modalState: 'open',
            content: (
              <ModalDirectorDetailsForm
                actionType='add'
                action={action}
                tableConfig={tableConfig}
                postUrl={getEndpointUrl('DeliverableAOF/addcompanydirector04')}
                applicationId={applicationId}
              />
            ),
            removeFooter: true,
          })
        }
        disabled={checkMode('readOnly')}
      />
      {tableData?.length !== 0 && (
        <Table
          tableData={tableData}
          tableColumns={tableColumns}
          headerCellMaxWidth='100px'
          bodyCellMaxWidth='100px'
          tableReadOnly={checkMode('readOnly')}
        />
      )}
      {genericError.error && (
        <Popup
          removeSelfClose
          type='error'
          label={genericError.message}
          popupCloseHelperFunction={() => setGenericError({error: false, message: ''})}
          click={() => setGenericError({error: false, message: ''})}
        />
      )}
      <FormFooter noOfButtons={2}>
        <FormFooterActionsLeft>
          <Button
            label={t('onboarding.main.backButton')}
            version={2}
            disabled={props.disablePrev}
            onClick={props.prevStep}
          />
        </FormFooterActionsLeft>
        <FormFooterActionsRight>
          <Button
            label={props.isLastForm ? t('onboarding.main.saveAndSubmit') : t('onboarding.main.continueButton')}
            disabled={tableData?.length === 0}
            onClick={submitForm}
          />
        </FormFooterActionsRight>
      </FormFooter>
    </FormStyle>
  )
}

export default CompanyDirectorsForm
